import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQuery } from 'react-query';
import { getLocationOrgIds, getLocationsForOrgIds, getOrgsData } from '@frontend/api-location';
import { TenantApi } from '@frontend/api-tenants';
import { authnClient } from '@frontend/auth';
import {
  clearLastVisitedPage,
  getDecodedWeaveToken,
  getJTI,
  getLoginData,
  getUser,
  getWeaveToken,
  isWeaveTokenActive,
  isWeaveTokenBufferActive,
  isWeaveUser,
  localStorageHelper,
  LOGIN_DATA_KEY,
  onWeaveTokenChange,
  PortalUser,
} from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import { useFetch } from '@frontend/fetch';
import { useSyncShellHistory } from '@frontend/history';
import { useTranslation } from '@frontend/i18n';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useOnboardingRedirect } from '@frontend/onboarding';
import { useMerchants } from '@frontend/payments-hooks';
import { useInitializePhones } from '@frontend/phone';
import {
  locationDataAugmentationPipeline,
  processOrgData,
  rebase,
  useAppScopeStore,
  useAppScopeStoreWithSetters,
  useScopedAppFlagStore,
  WeaveLocation,
  WeaveLocationGroup,
} from '@frontend/scope';
import { shell, useIpcShellProvider } from '@frontend/shell-utils';
import { sentry } from '@frontend/tracking';
import { useInitializePreferences, usePreference, usePreferenceStore } from '@frontend/user-preferences';
import { useAlert } from '@frontend/design-system';
import { signOut } from '../helpers/sign-out';
import { useApp } from '../providers/app.provider';
import { useConfigureLocation, useConfigureWeTags, useHandleScopeChange } from '../utils/configure-location';
import { setCookie } from '../utils/set-cookie';
import {
  retryWithTrace,
  handle401Error,
  chaosMonkeyMiddleware,
  chaosMonkeyWithMemoryMiddleware,
} from './http-interceptors';
import { useNewUserOnboardingStore } from './new-user-onboarding/store';
import { getShellThemeFromLocalStorage } from './shell-theme-switcher/helper';

// Import the extracted interceptors
export type AppStateType =
  | {
      state: 'ONBOARDING';
      meta: {
        onboardingRedirectUrl: string;
      };
    }
  | {
      state: 'LOADING';
    }
  | {
      state: 'LOCATION_SELECTION';
    }
  | {
      state: 'INITIALIZED';
    }
  | {
      state: 'REDIRECTING';
    };

export const useProcessScopeDataFromOrgIds = ({
  orgIds,
  allowedLocationIds,
}: {
  orgIds: string[];
  allowedLocationIds?: string[];
}) => {
  const { data: phoneTenants, isSuccess: tenantSuccess } = useQuery({
    queryKey: ['new-tenants', { orgIds }],
    queryFn: () => TenantApi.getTenantsWithOrgIds(orgIds),
    select: (data) => data.flatMap((t) => t.tenants),
    enabled: orgIds.length > 0,
  });

  const { data: locations, isSuccess: locationSuccess } = useQuery({
    queryKey: ['new-locations', { orgIds }],
    queryFn: async () => {
      return await getLocationsForOrgIds(orgIds);
    },
    select: (data) =>
      data
        .flatMap((l) => l.locations)
        // This is a dumb filter to satisfy TypeScript
        .filter(Boolean)
        .filter((l) => l.active)
        .map(rebase)
        .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0)),
    enabled: orgIds.length > 0,
  });

  const { data: orgData = [], isSuccess: orgSuccess } = useQuery({
    queryKey: ['new-orgs-data', { orgIds }],
    queryFn: async () => {
      if (!orgIds || !orgIds.length) return [];

      const orgsData = await getOrgsData(orgIds);
      return orgsData;
    },
    enabled: !!orgIds && !!orgIds.length,
  });

  const { locationList, locationMap, groupMap, locationHierarchy } = useMemo(() => {
    if (locations && phoneTenants) {
      const {
        locationList: list,
        locationMap: map,
        groupMap: gMap,
        hierarchy,
      } = locationDataAugmentationPipeline({
        locations: locations as WeaveLocation[],
        tenants: phoneTenants,
        allowedLocationIds,
      });

      return {
        locationList: list,
        locationMap: map,
        groupMap: gMap,
        locationHierarchy: hierarchy,
      };
    }

    return {
      locationList: [],
      locationMap: {},
      groupMap: {},
      locationHierarchy: {},
    };
  }, [locations, phoneTenants, allowedLocationIds]);

  const { orgMap } = useMemo(
    () =>
      processOrgData({
        orgData,
        locationHierarchy,
        groupMap,
      }),
    [orgData, locationHierarchy, groupMap]
  );

  return useMemo(
    () => ({
      isSuccess: (tenantSuccess && orgSuccess && locationSuccess) || (isWeaveUser() && orgIds.length === 0),
      orgMap,
      locationList,
      locationMap,
      groupMap,
      locationHierarchy,
    }),
    [
      tenantSuccess,
      orgSuccess,
      locationSuccess,
      orgMap,
      locationList,
      locationMap,
      groupMap,
      locationHierarchy,
      orgIds.length,
    ]
  );
};

/**
 * For weave users, we can use the latestOrganizationId to determine the initial orgIds.
 * For non-weave users, we need to make a request to get the orgIds.
 */
const useInitialOrgIds = ({ user }: { user?: PortalUser }) => {
  const { data: orgIds = [] } = useQuery({
    queryKey: ['orgIds', user?.userID],
    queryFn: async () => getLocationOrgIds(),
    select: (data) => data.orgId,
    enabled: user?.type !== 'weave',
  });

  const loginData = user ? getLoginData(user.userID) : undefined;
  const weaveUserOrgId = loginData?.latestOrganizationId;
  const resolvedOrgIds = useMemo(
    () => (user?.type === 'weave' && weaveUserOrgId ? [weaveUserOrgId] : orgIds),
    [orgIds, weaveUserOrgId, user?.type]
  );

  return resolvedOrgIds;
};

export const determineInitialLocations = ({
  locationMap,
  groupMap,
}: {
  locationMap: Record<string, WeaveLocation>;
  groupMap: Record<string, WeaveLocationGroup>;
}) => {
  const user = getUser();
  const decodedWeaveToken = getDecodedWeaveToken();
  const isWeaveUser = user?.type === 'weave';
  const keys = Object.keys(decodedWeaveToken?.ACLS ?? {});
  const loginData = getLoginData(user?.userID ?? '');

  let latestLocationIds: string[] = [];
  if (isWeaveUser) {
    latestLocationIds = loginData?.latestLocationIds ?? [];
  } else {
    /**
     * We cannot rely on `keys` to determine if the user has access to only one location. A user who has access to a parent and it's child will have 2 keys.
     * We need to filter the keys based on the locationMap to determine if the user has access to only one location.
     *
     * If we've determined that the user has access to only one location, we can set that as the latestLocationId.
     * If the user has access to multiple locations, we can use the latestLocationIds from the loginData.
     */
    const accessibleLocationIds = Object.values(locationMap)
      .filter((location) => keys.includes(location.id) && location.accessible)
      .map((location) => location.id);

    const accessibleGroupIds = Object.values(groupMap)
      .filter(
        // If a group has ZERO children the user can access, we will consider that group as a valid location for now
        (group) => keys.includes(group.id) && group.accessible && !group.children.some((child) => child.accessible)
      )
      .map((group) => group.id);

    const combined = accessibleGroupIds.concat(accessibleLocationIds);

    if (combined.length === 1) {
      /**
       * Give priority to the locations first. If there's only one location, use that.
       * If there are no locations, we can use the groups.
       */
      latestLocationIds = [combined[0]];
    } else {
      latestLocationIds =
        loginData?.latestLocationIds?.filter(
          // allow parent locations to be selected as well
          (id) => accessibleLocationIds.includes(id) || accessibleGroupIds.includes(id)
        ) ?? [];
    }
  }

  /**
   * Filter this list so that we only have locations that exist in the locationMap.
   * These ids should be valid locations that the user has access to, because we're getting them from either loginData or keys
   */
  // allow parent locations to be selected as well
  latestLocationIds = latestLocationIds.filter((id) => locationMap[id] || groupMap[id]);

  const needsOrgId = latestLocationIds.length > 0;

  // If there is no valid location, we don't want the org id to be set, so that the picker doesn't have an org selected with invalid locations
  const lastOrgId = needsOrgId
    ? // allow parent locations to be selected as well
      locationMap[latestLocationIds[0]]?.orgId || groupMap[latestLocationIds[0]]?.orgId
    : undefined;

  return {
    latestLocationIds,
    lastOrgId,
  };
};

export const useInitializeAppScopeStore = () => {
  const { setLastUsedVertical } = useLastUsedVerticalShallowStore('setLastUsedVertical');
  const {
    setOrgData,
    setLocationData,
    setGroupData,
    setSelectedGroupId,
    setSelectedLocationIds,
    setSelectedOrgId,
    setScopeHierarchy,
  } = useAppScopeStoreWithSetters();
  const user = getUser();
  const decodedWeaveToken = getDecodedWeaveToken();
  const isWeaveUser = decodedWeaveToken?.type === 'weave';
  const keys = useMemo(() => Object.keys(decodedWeaveToken?.ACLS ?? {}), [decodedWeaveToken]);

  const { configureLocationData } = useConfigureLocation();
  const [isComplete, setIsComplete] = useState(false);
  const orgIdsForLocationsQuery = useInitialOrgIds({ user });

  const { isSuccess, orgMap, locationMap, groupMap, locationHierarchy } = useProcessScopeDataFromOrgIds({
    orgIds: orgIdsForLocationsQuery,
    allowedLocationIds: isWeaveUser ? undefined : keys,
  });

  const { latestLocationIds, lastOrgId } = useMemo(
    () =>
      determineInitialLocations({
        locationMap,
        groupMap,
      }),
    [locationMap]
  );

  function setInitialValues() {
    const scopeHierarchy = Object.entries(locationHierarchy).reduce((acc, [groupId, locations]) => {
      const group = groupMap[groupId];

      if (!group.orgId) return acc;

      if (acc[group.orgId]) {
        acc[group.orgId][group.id] = locations;
      } else {
        acc[group.orgId] = {
          [group.id]: locations,
        };
      }

      return acc;
    }, {} as Record<string, Record<string, string[]>>);

    setGroupData(groupMap);
    setLocationData(locationMap);
    setOrgData(orgMap);
    setScopeHierarchy(scopeHierarchy);

    if (lastOrgId) {
      setSelectedOrgId(lastOrgId);
    }

    if (
      latestLocationIds &&
      latestLocationIds.length > 0 &&
      (locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]])
    ) {
      if ((locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]]).vertical) {
        setLastUsedVertical((locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]]).vertical as Vertical);
      }

      setSelectedLocationIds(latestLocationIds, { skipValidation: true });
      const groupId = locationMap[latestLocationIds[0]]?.groupId ?? groupMap[latestLocationIds[0]]?.id ?? '';
      setSelectedGroupId(groupId, { skipValidation: true });

      // DEPRECATE THIS
      const newLocationId = latestLocationIds[0];
      configureLocationData({
        newLocationId,

        onError: () => {
          // If the location is not found, clear the login data and refresh the page
          const loginData: string | undefined = localStorageHelper.get(LOGIN_DATA_KEY);
          if (loginData) {
            // we don't want a user to be stuck in a loop of refreshing the page
            localStorageHelper.delete(LOGIN_DATA_KEY);
            location.reload();
          }
        },
      });
    }
  }

  useEffect(() => {
    if (isSuccess && !isComplete) {
      setInitialValues();
      setIsComplete(true);
    }
  }, [isSuccess]);

  return {
    storeInitialized: isComplete,
  };
};

export const createRefreshTokenMiddleware = (deps: {
  setAuthorizationHeader: (token: string, jtiToken: string) => void;
  alerts: { error: (msg: string) => void };
  t: (msg: string) => string;
  triggerInactiveSignOut: () => void;
  getCurrentUseJti: () => boolean;
  setUseJti: (useJti: boolean) => void;
}) => {
  return async (req: Request): Promise<Request> => {
    const useJTI = await authnClient.shouldUseJTI();
    if (deps.getCurrentUseJti() !== useJTI) {
      deps.setUseJti(useJTI);
    }

    // TODO remove this and let the error interceptor handle logout
    if (!authnClient.isUserAuthenticated() && !isWeaveTokenBufferActive()) {
      deps.triggerInactiveSignOut();
    }

    if (!isWeaveTokenActive() && isWeaveTokenBufferActive()) {
      try {
        const refreshResponse = await authnClient.refreshWeaveToken();
        req.headers.set('Authorization', `Bearer ${useJTI ? refreshResponse.jtiToken : refreshResponse.token}`);
        deps.setAuthorizationHeader(refreshResponse.token || '', refreshResponse.jtiToken || '');
      } catch (error: any) {
        if (error?.status >= 500) {
          sentry.error({
            topic: 'auth',
            team: 'users-infra',
            error: 'Refresh token failed in middleware',
          });
        }
        deps.alerts.error(deps.t("You've been signed out"));
        clearLastVisitedPage();
        return signOut().then(() => req);
      }
    }

    const currAuthHeader = req.headers.get('Authorization');
    if (useJTI) {
      const localStorageJtiToken = getJTI();
      if (currAuthHeader !== `Bearer ${localStorageJtiToken}`) {
        req.headers.set('Authorization', `Bearer ${localStorageJtiToken}`);
      }
    } else {
      const localStorageWeaveToken = getWeaveToken();
      if (currAuthHeader !== `Bearer ${localStorageWeaveToken}`) {
        req.headers.set('Authorization', `Bearer ${localStorageWeaveToken}`);
      }
    }

    return req;
  };
};

export const createResponseInterceptor401Middleware = (deps: {
  getCurrentUseJti: () => boolean;
  getJTI: () => string | undefined;
  getWeaveToken: () => string | undefined;
  setAuthorizationHeader: (token: string, jtiToken: string) => void;
  setUseJti: (useJti: boolean) => void;
}) => {
  return async function responseInterceptor401(response: Response, requestCopy: Request): Promise<Response> {
    if (response.ok) {
      return Promise.resolve(response);
    }

    const useJTI = await authnClient.shouldUseJTI();
    if (deps.getCurrentUseJti() !== useJTI) {
      deps.setUseJti(useJTI);
    }

    if (response.status === 401) {
      try {
        const refreshResponse = await authnClient.refreshWeaveToken();
        deps.setAuthorizationHeader(refreshResponse.token || '', refreshResponse.jtiToken || '');
        const currAuthHeader = requestCopy.headers.get('Authorization');
        const weaveToken = refreshResponse.token;
        const jtiToken = refreshResponse.jtiToken;

        if (useJTI) {
          if (currAuthHeader !== `Bearer ${jtiToken}`) {
            requestCopy.headers.set('Authorization', `Bearer ${jtiToken}`);
          }
        } else {
          if (currAuthHeader !== `Bearer ${weaveToken}`) {
            requestCopy.headers.set('Authorization', `Bearer ${weaveToken}`);
          }
        }
      } catch (e) {
        sentry.error({ topic: 'auth', error: 'Refresh token failed in response interceptor', team: 'users-infra' });
        // allow a fall through, the 401 error handler will take care of what is needed here.
      }
    }
    return Promise.resolve(response);
  };
};

export const useHttpSetup = ({
  // appVersion,
  // shellVersion,
  token,
  jtiToken,
  isSessionTokenEnabled,
}: {
  appVersion?: string;
  shellVersion?: string;
  token?: string;
  jtiToken?: string;
  isSessionTokenEnabled: boolean;
}) => {
  const isSignOutTriggered = useRef(false);
  const { t } = useTranslation();
  const alerts = useAlert();
  const isSessionTokenEnabledRef = useRef(false);
  const [chaosBlockedURLS, setChaosBlockedURLS] = useState<string[]>([]);
  const [chaosAllowedURLS, setChaosAllowedURLS] = useState<string[]>([]);

  const {
    setAuthorizationHeader,
    addRequestInterceptor,
    removeRequestInterceptor,
    removeErrorInterceptor,
    addErrorInterceptor,
    addResponseInterceptor,
    removeResponseInterceptor,
    configureRetryer,
    getCurrentUseJti,
    setUseJti,
    // setHeader,
    // deleteHeader,
  } = useFetch();

  /**
   * TODO: this needs to wait until all services update monorail in order to respect these headers
   * If we push these now, we will get CORs errors 
  useEffect(() => {
    if (shellVersion) {
      setHeader('X-Shell-Version', shellVersion);
    } else {
      deleteHeader('X-Shell-Version');
    }

    if (appVersion) {
      setHeader('X-App-Version', appVersion);
    } else {
      deleteHeader('X-App-Version');
    }
  }, [setHeader, deleteHeader, shellVersion, appVersion]);
    */

  const triggerInactiveSignOut = () => {
    if (!isSignOutTriggered.current) {
      alerts.error(t('You have been signed out due to inactivity. Please sign in again.'));
      isSignOutTriggered.current = true;
      setTimeout(() => {
        clearLastVisitedPage();
        signOut();
        isSignOutTriggered.current = false;
      }, 2000);
    }
  };

  const refreshTokenMiddleware = createRefreshTokenMiddleware({
    setAuthorizationHeader,
    alerts,
    t,
    triggerInactiveSignOut,
    getCurrentUseJti,
    setUseJti,
  });

  // Use the extracted handler401Error function from http-interceptors.ts
  const handleError401 = handle401Error(triggerInactiveSignOut, isWeaveTokenBufferActive);

  const responseInterceptor401 = createResponseInterceptor401Middleware({
    getCurrentUseJti: getCurrentUseJti,
    getJTI: getJTI,
    getWeaveToken: getWeaveToken,
    setAuthorizationHeader,
    setUseJti: setUseJti,
  });

  useEffect(() => {
    addRequestInterceptor({ id: 'refresh-token', interceptor: refreshTokenMiddleware });
    if (localStorage.getItem('chaosMemory')) {
      addRequestInterceptor({
        id: 'chaos-monkey-memory',
        interceptor: chaosMonkeyMiddleware,
      });
    }
    if (localStorage.getItem('chaosMonkey')) {
      addRequestInterceptor({
        id: 'chaos-monkey-basic',
        interceptor: chaosMonkeyWithMemoryMiddleware(
          chaosBlockedURLS,
          chaosAllowedURLS,
          setChaosBlockedURLS,
          setChaosAllowedURLS
        ),
      });
    }
    configureRetryer(retryWithTrace);
    setAuthorizationHeader(token ?? '', jtiToken ?? '');
    addErrorInterceptor({ id: '401', interceptor: handleError401 });
    const clearTokenChangeEvent = onWeaveTokenChange((newToken: string, jtiToken: string) => {
      setAuthorizationHeader(newToken, jtiToken);
    });
    addResponseInterceptor({ id: '401', interceptor: responseInterceptor401 });

    return () => {
      removeRequestInterceptor('chaos-monkey');
      removeRequestInterceptor('refresh-token');
      removeResponseInterceptor('401');
      removeErrorInterceptor('401');
      clearTokenChangeEvent();
    };
  }, []);

  useEffect(() => {
    isSessionTokenEnabledRef.current = isSessionTokenEnabled;
  }, [isSessionTokenEnabled]);
};

export const useInitializeApp = (): AppStateType => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { version: shellVersion } = useIpcShellProvider();
  const { currentVersion: appVersion } = useApp();
  const { selectedLocationIds, selectedGroupId } = useAppScopeStore();
  const isSessionTokenEnabled = getFeatureFlagValue('use-session-token');
  const weaveToken = getWeaveToken();
  const jtiToken = getJTI();

  useHttpSetup({
    token: weaveToken,
    jtiToken: jtiToken,
    isSessionTokenEnabled,
    appVersion: appVersion,
    shellVersion: shellVersion,
  });

  // This hook initializes user preferences and triggers the GET API call to fetch all user settings.
  useInitializePreferences();
  useRemoveAppLoader();
  const locationId = selectedLocationIds[0] ?? selectedGroupId;
  const { storeInitialized } = useInitializeAppScopeStore();
  const { flagsInitialized } = useHandleScopeChange();
  const { weTagsInitialized } = useConfigureWeTags();
  const { isNewUserOnboardingComplete } = useNewUserOnboardingStore('isNewUserOnboardingComplete');
  const [ExistingUser] = usePreference('ExistingUser', { omitDefaultValue: true });
  const isDataInitialized = usePreferenceStore((state) => state.isDataInitialized);
  const isDataError = usePreferenceStore((state) => state.isDataError);
  useSyncShellHistory();
  useMerchants('selectedLocationIds');
  useInitializePhones({ ready: true });
  setCookie({ name: 'isWeaveCustomer', value: 'true' });
  const { shouldShowOnboardingLoader, onboardingRedirectUrl } = useOnboardingRedirect();

  useLayoutEffect(() => {
    getShellThemeFromLocalStorage();
  }, []);

  useRemoveAppLoader();
  if (shouldShowOnboardingLoader || onboardingRedirectUrl) {
    return {
      state: shouldShowOnboardingLoader ? 'LOADING' : 'ONBOARDING',
      meta: {
        onboardingRedirectUrl,
      },
    };
  } else if (
    !storeInitialized ||
    !flagsInitialized ||
    !weTagsInitialized ||
    (shell.isShell && !isDataInitialized && !isDataError)
  ) {
    return {
      state: 'LOADING',
    };
  } else if (
    !locationId ||
    (shell.isShell && !ExistingUser && isDataInitialized && !isDataError && !isNewUserOnboardingComplete)
  ) {
    return {
      state: 'LOCATION_SELECTION',
    };
  } else if (locationId && storeInitialized && flagsInitialized) {
    return {
      state: 'INITIALIZED',
    };
  }
  return {
    state: 'LOADING',
  };
};
